import React, { useEffect } from 'react';
import { Panels } from '../Consts';
import {
  Button, Card, CardGrid, Cell, Div,
  FixedLayout, Group, Panel, PanelHeaderButton, Placeholder,
  PullToRefresh, Skeleton, Tabs, TabsItem,
} from '@vkontakte/vkui';
import { Icon28Profile } from '@vkontakte/icons';
import { UserAvatar } from '../components/UserAvatar';
import { useAppContext } from '../../AppContext';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { prepareString } from '../../helpers/helpers';
import { useAppStore } from '../../AppStore';
import { getRouteEventInfo, getRouteEventsList, getRouteProfile } from '../../route';
import { SearchComponent } from './components/SearchComponent';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';

const CURRENT_PANEL = Panels.EVENTS_ARCHIVE_LIST;

const Header = () => {
  const context = useAppContext();

  return <PlatformPanelHeader
    text={<Tabs mode={"default"} layoutFillMode={"shrinked"}>
      <TabsItem
        id="events"
        onClick={() => context.router.replace(...getRouteEventsList())}
        selected={false}
      >
        {context.lang().getKey(Panels.EVENTS_LIST, 'header')}
      </TabsItem>
      <TabsItem
        id="archive"
        selected={true}
      >
        {context.lang().getKey(CURRENT_PANEL, 'header')}
      </TabsItem>
    </Tabs>}
    isShowBack={false}
    before={
      <PanelHeaderButton
        onClick={() => context.router.push(...getRouteProfile())}
        aria-label={context.lang().getKey(CURRENT_PANEL, 'profile_button_label')}
      >
        <Icon28Profile />
      </PanelHeaderButton>
    }
  />
}

const Item = ({event}) => {
  const context = useAppContext();

  return (<CardGrid size="l">
    <Card>
      <Cell
        before={<UserAvatar src={event.image_s} name={event.name} id={event.id} />}
        onClick={(e) => context.router.push(...getRouteEventInfo(event.id))}
      >
        {event.name}
      </Cell>
    </Card>
  </CardGrid>);
}

const EmptyList = () => {
  const context = useAppContext();

  return (<Placeholder
    className={'EventEmptyList__block'}
    header={<>
      {context.lang().getKey(CURRENT_PANEL, 'emptyListContentLine1')}<br />
      {context.lang().getKey(CURRENT_PANEL, 'emptyListContentLine2')}
    </>}
  />);
}

const SearchEmptyList = () => {
  const context = useAppContext();

  return (<Placeholder
    className={'SearchEmptyList__block'}
    header={context.lang().getKey(CURRENT_PANEL, 'searchEmptyList')}
  />);
}

const LoadMoreButton = ({isLoading, onClick}) => {
  const context = useAppContext();

  return (<Div>
    <Button
      size="s"
      mode="tertiary"
      stretched={true}
      loading={isLoading}
      onClick={onClick}
    >
      {context.lang().getKey(CURRENT_PANEL, 'moreLoadButton')}
    </Button>
  </Div>)
}

const SceletonPanel = () => {
  const context = useAppContext();

  return (<Panel nav={CURRENT_PANEL} className={'SceletonPanel'}>
    <PlatformPanelHeader
      text={<Skeleton width={170} />}
      isShowBack={false}
      before={
        <PanelHeaderButton
          aria-label={context.lang().getKey(CURRENT_PANEL, 'profile_button_label')}
        >
          <Skeleton width={28} height={28} borderRadius="50%" />
        </PanelHeaderButton>
      }
    />
    <Div style={{paddingBottom:0}}>
      <Skeleton width="100%" height={32}/>
    </Div>
    <Group mode="plain">
      {[1,2,3,4,5,6,7,8,9,10].map((i) => (
        <CardGrid size="l" key={i}>
          <Card>
            <Cell
              before={<Skeleton width={48} height={48} borderRadius="50%" />}
              after={<Skeleton width={24} height={24} borderRadius="50%" />}
              subtitle={<Skeleton width={i*10+10} />}
            >
            </Cell>
          </Card>
        </CardGrid>
      ))}
    </Group>
    <FixedLayout vertical="bottom" style={{textAlign: 'center'}}>
      <Div>
        <Skeleton width="100%" height={36} />
      </Div>
    </FixedLayout>
  </Panel>);
}

const EventsArchiveListPanel = ({}) => {
  const context = useAppContext();
  const appStore = useAppStore();

  const [search, setSearch] = React.useState('');
  const [events, setEvents] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);

  const [searchEvents, setSearchEvents] = React.useState([]);
  const [searchTotalCount, setSearchTotalCount] = React.useState(0);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [error, setError] = React.useState(null);

  const searchTimeout = React.useRef(0);
  const [isRefreshFetching, setIsRefreshFetching] = React.useState(false);


  const fetch = (offset, query) => {
    return new Promise((resolve, reject) => {
      setIsLoading(true);
      context.httpClient()
        .getEvents(offset, query, true)
        .then((response) => {
          if (query) {
            if (offset > 0) {
              setSearchEvents([...(searchEvents || []), ...(response.data.events || [])]);
            } else {
              setSearchEvents(response.data.events);
            }
            setSearchTotalCount(response.data.count);
          } else {
            if (offset > 0) {
              setEvents([...(events || []), ...(response.data.events || [])]);
            } else {
              setEvents(response.data.events);
            }
            setTotalCount(response.data.count);
          }
          setIsLoading(false);
          setError(null);
          resolve(response);
        })
        .catch((e) => {
          setIsLoading(false);
          setError(e);
          context.showError(e);
          reject(e);
        });
    });
  }

  const onRefresh = () => {
    setIsRefreshFetching(true);
    fetch(0, search)
      .finally(() => {
        setIsRefreshFetching(false);
        setIsLoaded(true);
      })
  }
  const onSearch = (value) => {
    if (value.length < 30) {
      setSearch(value);
    }

    if (value.length < 1) {
      return setSearchEvents([]);
    }

    const searchValue = prepareString(value).toLowerCase();
    if (events.length >= totalCount) {
      //все события загружены можем искать по списку
      return setSearchEvents(events.filter((event) => prepareString(event.name).toLowerCase().indexOf(searchValue) > -1));
    }

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetch(0, prepareString(searchValue).toLowerCase());
    }, 500);
  }
  const loadMore = () => fetch(events.length, search);

  useEffect(() => {
    context.init()
      .then(() => {
        fetch(events.length, search)
          .finally(() => {
            setIsLoaded(true);
          });
      })
      .catch((e) => {
        setError(e);
        setIsLoading(false);
        context.showError(e);
      });
  }, [])

  if (error) {
    return (<ErrorLoadComponent
      error={error}
      onFetch={onRefresh}
    />)
  }

  if (!isLoaded) {
    return (<SceletonPanel />);
  }

  return (
    <Panel nav={CURRENT_PANEL}>
      <Header />
      {context.snackbar}
      <SearchComponent
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={context.lang().getKey(CURRENT_PANEL, 'searchPlaceholder')}
      />
      <PullToRefresh onRefresh={onRefresh} isFetching={isRefreshFetching}>
        <Group mode="plain" style={{marginBottom: 40}}>
          {
            events.length === 0 && !search && (<EmptyList />)
          }
          {
            (search.length > 0 ? searchEvents : events).map((event) => (<Item key={event.id} event={event} />))
          }
          {
            (search.length > 0 && searchEvents.length < 1) ? (<SearchEmptyList />) : undefined
          }
          {
            (search.length > 0 ? searchEvents.length : events.length) < (search.length > 0 ? searchTotalCount : totalCount) ? (<LoadMoreButton isLoading={isLoading} onClick={loadMore} />) : undefined
          }
        </Group>
      </PullToRefresh>
    </Panel>
  );
}

export default EventsArchiveListPanel;
